const esc = (s) => String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
/**
 * Builds full HTML document for Building Permit form print/download.
 * Shared by Forms.tsx (Forms Register download) and JobFormView (surveyor-facing permit view).
 */
export function buildFormPrintHtml(form, options = {}) {
    const { title, autoPrint = true, dataOverrides } = options;
    const pageTitle = esc(title ?? form.name ?? "Building Permit Form");
    const formData = {
        ...(form.data || {}),
        ...dataOverrides,
    };
    const label = (key, fallback) => esc(formData.fieldLabels?.[key] || fallback);
    const val = (v) => esc(v);
    const applicantRoleVal = formData.applicantRole === "owner"
        ? "Owner"
        : formData.applicantRole === "agent_of_owner"
            ? "Agent of Owner"
            : "";
    const prop = formData.propertyDetails;
    const owner = formData.buildingOwner;
    const builder = formData.builder;
    const builderNat = builder?.naturalPerson;
    const customTitle = val(formData.customFormTitle) ||
        esc(form.templateName || "Building Permit Form");
    const subtitleDateEscaped = val(formData.customSubtitleDate) ||
        "16/01/2026";
    const regulationText = `APPLICATION FOR A BUILDING PERMIT BUILDING ACT 1993, BUILDING REGULATIONS 2018 REGULATION 24 Form 1 (As from ${subtitleDateEscaped})`;
    return `<!DOCTYPE html>
<html>
<head>
  <title>${pageTitle}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; color: #333; }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #e5e7eb; }
    .title { font-size: 24px; font-weight: bold; color: #1f2937; }
    .subtitle { font-size: 16px; color: #374151; margin-top: 4px; }
    .subtitle-secondary { font-size: 14px; color: #374151; margin-top: 2px; font-weight: 600; }
    .company-logo { text-align: right; }
    .company-logo img { max-height: 50px; margin-bottom: 5px; }
    .company-name { font-size: 12px; color: #6b7280; text-transform: lowercase; }
    .section-title { font-size: 16px; font-weight: bold; margin: 20px 0 15px; color: #374151; }
    .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
    .form-field { margin-bottom: 15px; }
    .form-label { display: block; font-size: 12px; font-weight: 600; color: #4b5563; margin-bottom: 5px; }
    .form-value { display: block; padding: 8px 12px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 14px; min-height: 38px; }
    .form-textarea { min-height: 100px; width: 100%; }
    .checkbox-section { margin: 20px 0; padding: 15px; background: #f9fafb; border-radius: 4px; }
    .checkbox-label { display: flex; align-items: flex-start; gap: 10px; }
    .form-note { font-size: 11px; color: #6b7280; margin-top: 8px; font-style: italic; }
    .accordion-section { margin: 20px 0; }
    .accordion-title { font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 15px; }
    @media print { body { padding: 20px; } .no-print { display: none; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">${customTitle}</div>
      <div class="subtitle">${regulationText}</div>
      <div class="subtitle-secondary">APPLICATION FORM FOR A BUILDING PERMIT AND AGREEMENT</div>
    </div>
    <div class="company-logo">
      <img src="${val(formData.customLogo || "/logo.png")}" alt="${val(formData.customCompanyName || "Group Building Surveyors")}" />
      <div class="company-name">${val(formData.customCompanyName || "group building surveyors")}</div>
    </div>
  </div>

  <div class="section-title">To RBS: ${val(formData.recipient || "Not specified")}</div>
  <div class="section-title">From: Applicants details:</div>
  <div class="form-grid">
    ${formData.applicantRole ? `<div class="form-field"><span class="form-label">${label("applicantRole", "*Owner / *Agent of Owner")}</span><span class="form-value">${applicantRoleVal}</span></div>` : ""}
    ${formData.applicantAcnArbn ? `<div class="form-field"><span class="form-label">${label("applicantAcnArbn", "*ACN/*ARBN:")}</span><span class="form-value">${val(formData.applicantAcnArbn)}</span></div>` : ""}
    ${formData.contactName ? `<div class="form-field"><span class="form-label">${label("contactName", "Contact name:")}</span><span class="form-value">${val(formData.contactName)}</span></div>` : ""}
    ${formData.email ? `<div class="form-field"><span class="form-label">${label("email", "E-mail:")}</span><span class="form-value">${val(formData.email)}</span></div>` : ""}
    ${formData.companyName ? `<div class="form-field"><span class="form-label">${label("companyName", "Company name:")}</span><span class="form-value">${val(formData.companyName)}</span></div>` : ""}
    ${formData.phone ? `<div class="form-field"><span class="form-label">${label("phone", "Phone:")}</span><span class="form-value">${val(formData.phone)}</span></div>` : ""}
    ${formData.address ? `<div class="form-field"><span class="form-label">${label("address", "Postal address of applicant:")}</span><span class="form-value">${val(formData.address)}</span></div>` : ""}
    ${formData.servingAddress ? `<div class="form-field"><span class="form-label">${label("servingAddress", "Address for serving of documents:")}</span><span class="form-value">${val(formData.servingAddress)}</span></div>` : ""}
  </div>

  <div class="section-title">Property details:</div>
  <div class="form-grid">
    ${prop?.buildingName ? `<div class="form-field"><span class="form-label">${label("propertyBuildingName", "Building name:")}</span><span class="form-value">${val(prop.buildingName)}</span></div>` : ""}
    ${prop?.shopNo ? `<div class="form-field"><span class="form-label">${label("propertyShopNo", "Shop No:")}</span><span class="form-value">${val(prop.shopNo)}</span></div>` : ""}
    ${prop?.fullAddress ? `<div class="form-field"><span class="form-label">${label("propertyFullAddress", "Full address:")}</span><span class="form-value">${val(prop.fullAddress)}</span></div>` : ""}
    ${prop?.lotNumber ? `<div class="form-field"><span class="form-label">${label("propertyLotNumber", "Lot number:")}</span><span class="form-value">${val(prop.lotNumber)}</span></div>` : ""}
    ${prop?.lpPs ? `<div class="form-field"><span class="form-label">${label("propertyLpPs", "LP/PS:")}</span><span class="form-value">${val(prop.lpPs)}</span></div>` : ""}
    ${prop?.vol ? `<div class="form-field"><span class="form-label">${label("propertyVol", "Vol:")}</span><span class="form-value">${val(prop.vol)}</span></div>` : ""}
    ${prop?.fol ? `<div class="form-field"><span class="form-label">${label("propertyFol", "Fol:")}</span><span class="form-value">${val(prop.fol)}</span></div>` : ""}
    ${prop?.crownAllot ? `<div class="form-field"><span class="form-label">${label("propertyCrownAllot", "Crown Allot:")}</span><span class="form-value">${val(prop.crownAllot)}</span></div>` : ""}
    ${prop?.section ? `<div class="form-field"><span class="form-label">${label("propertySection", "Section:")}</span><span class="form-value">${val(prop.section)}</span></div>` : ""}
    ${prop?.parish ? `<div class="form-field"><span class="form-label">${label("propertyParish", "Parish:")}</span><span class="form-value">${val(prop.parish)}</span></div>` : ""}
    ${prop?.county ? `<div class="form-field"><span class="form-label">${label("propertyCounty", "County:")}</span><span class="form-value">${val(prop.county)}</span></div>` : ""}
    ${prop?.council ? `<div class="form-field"><span class="form-label">${label("propertyCouncil", "Council:")}</span><span class="form-value">${val(prop.council)}</span></div>` : ""}
  </div>
  <div class="checkbox-section">
    <div class="checkbox-label">
      <input type="checkbox" ${prop?.landOwnedByCrownOrPublicAuthority ? "checked" : ""} disabled />
      <span>Land owned by the Crown or public authority (tick box)</span>
    </div>
  </div>

  ${owner ? `
  <div class="accordion-section">
    <div class="accordion-title">Building owners details / Centre Management details</div>
    <div class="form-note" style="margin-bottom: 10px;">(If applicant is agent of owner)</div>
    <div class="form-grid">
      ${owner.name ? `<div class="form-field"><span class="form-label">${label("ownerName", "Name of owner(s)")}</span><span class="form-value">${val(owner.name)}</span></div>` : ""}
      ${owner.email ? `<div class="form-field"><span class="form-label">${label("ownerEmail", "E-mail:")}</span><span class="form-value">${val(owner.email)}</span></div>` : ""}
      ${owner.company ? `<div class="form-field"><span class="form-label">${label("ownerCompany", "Company name:")}</span><span class="form-value">${val(owner.company)}</span></div>` : ""}
      ${owner.phone ? `<div class="form-field"><span class="form-label">${label("ownerPhone", "Phone:")}</span><span class="form-value">${val(owner.phone)}</span></div>` : ""}
      ${owner.address ? `<div class="form-field"><span class="form-label">${label("ownerAddress", "Postal address:")}</span><span class="form-value">${val(owner.address)}</span></div>` : ""}
      ${owner.acnArbn || owner.acn ? `<div class="form-field"><span class="form-label">${label("ownerACN", "*ACN/ARBN:")}</span><span class="form-value">${val(owner.acnArbn || owner.acn)}</span></div>` : ""}
    </div>
  </div>
  ` : ""}

  <div class="checkbox-section" style="background: #fef3c7;">
    <div class="checkbox-label">
      <input type="checkbox" ${formData.lesseeResponsible ? "checked" : ""} disabled />
      <span style="font-weight: bold;">Lessee responsible for building work:</span>
    </div>
    <div class="form-note">
      Indicate if a lessee of the building, of which parts are leased by different persons, is responsible for the alterations to a part of the building leased by the lessee
    </div>
  </div>

  ${builder ? `
  <div class="accordion-section">
    <div class="accordion-title">Builder: (If known)</div>
    <div class="form-grid">
      ${builder.name ? `<div class="form-field"><span class="form-label">${label("builderName", "Name:")}</span><span class="form-value">${val(builder.name)}</span></div>` : ""}
      ${builder.contactPerson ? `<div class="form-field"><span class="form-label">${label("builderContact", "Contact person:")}</span><span class="form-value">${val(builder.contactPerson)}</span></div>` : ""}
      ${builder.email ? `<div class="form-field"><span class="form-label">${label("builderEmail", "E-mail:")}</span><span class="form-value">${val(builder.email)}</span></div>` : ""}
      ${builder.company ? `<div class="form-field"><span class="form-label">${label("builderCompany", "Company name:")}</span><span class="form-value">${val(builder.company)}</span></div>` : ""}
      ${builder.phone ? `<div class="form-field"><span class="form-label">${label("builderPhone", "Phone:")}</span><span class="form-value">${val(builder.phone)}</span></div>` : ""}
      ${builder.address ? `<div class="form-field"><span class="form-label">${label("builderAddress", "Address:")}</span><span class="form-value">${val(builder.address)}</span></div>` : ""}
      ${builder.practitionerNo ? `<div class="form-field"><span class="form-label">${label("builderPractitionerNo", "Building Practitioner No:")}</span><span class="form-value">${val(builder.practitionerNo)}</span></div>` : ""}
      ${builder.acnArbn ? `<div class="form-field"><span class="form-label">${label("builderAcnArbn", "*ACN/*ARBN:")}</span><span class="form-value">${val(builder.acnArbn)}</span></div>` : ""}
      ${builder.postalAddress ? `<div class="form-field"><span class="form-label">${label("builderPostalAddress", "Postal address:")}</span><span class="form-value">${val(builder.postalAddress)}</span></div>` : ""}
    </div>
    ${builderNat ? `
    <div class="section-title" style="font-size: 14px; margin-top: 16px;">
      * Natural person for service of directions, notices and orders (if builder is a body corporate / company):
    </div>
    <div class="form-grid">
      ${builderNat.contactPerson ? `<div class="form-field"><span class="form-label">${label("builderNaturalContactPerson", "Contact person:")}</span><span class="form-value">${val(builderNat.contactPerson)}</span></div>` : ""}
      ${builderNat.email ? `<div class="form-field"><span class="form-label">${label("builderNaturalEmail", "E-mail:")}</span><span class="form-value">${val(builderNat.email)}</span></div>` : ""}
      ${builderNat.address ? `<div class="form-field"><span class="form-label">${label("builderNaturalAddress", "Address:")}</span><span class="form-value">${val(builderNat.address)}</span></div>` : ""}
      ${builderNat.phone ? `<div class="form-field"><span class="form-label">${label("builderNaturalPhone", "Phone:")}</span><span class="form-value">${val(builderNat.phone)}</span></div>` : ""}
    </div>
    ` : ""}
    <div class="form-note" style="margin-top: 15px;">
      [If the builder is carrying out work under a major domestic building contract, attach an extract of the major domestic building contract showing the names of the parties to the contract in relation to the proposed building work and a copy of the certificate of insurance]
    </div>
  </div>
  ` : ""}

  ${(() => {
        const practitioners = formData.buildingPractitioners;
        if (!practitioners || practitioners.length === 0)
            return "";
        const hasAny = practitioners.some(p => p?.name || p?.categoryOrClass || p?.registrationNo);
        if (!hasAny)
            return "";
        return `
  <div class="accordion-section">
    <div class="accordion-title">Building Practitioners and or architect engaged to prepare documents for this permit:</div>
    <div class="form-note" style="margin-bottom: 10px;">[ List any building practitioner or architect engaged to prepare documents forming part of the application for this permit. ]</div>
    ${practitioners.map((p) => !p?.name && !p?.categoryOrClass && !p?.registrationNo ? "" : `
    <div class="form-grid" style="margin-bottom: 10px;">
      <div class="form-field"><span class="form-label">Name:</span><span class="form-value">${val(p?.name)}</span></div>
      <div class="form-field"><span class="form-label">Category / Class:</span><span class="form-value">${val(p?.categoryOrClass)}</span></div>
      <div class="form-field"><span class="form-label">Registration No:</span><span class="form-value">${val(p?.registrationNo)}</span></div>
    </div>
    `).join("")}
  </div>
    `;
    })()}

  ${(() => {
        const ownerBuilder = formData.ownerBuilderSection;
        if (!ownerBuilder)
            return "";
        return `
  <div class="accordion-section">
    <div class="accordion-title">Owner builder: (if applicable)</div>
    <div class="form-grid">
      <div class="form-field">
        <span class="form-label">I intend to carry out the work as an owner-builder:</span>
        <span class="form-value">${ownerBuilder.intendsOwnerBuilder ? "YES" : "NO"}</span>
      </div>
      ${ownerBuilder.ownerBuilderCertificateOfConsentNo ? `<div class="form-field"><span class="form-label">Owner-builder certificate of consent no:</span><span class="form-value">${val(ownerBuilder.ownerBuilderCertificateOfConsentNo)}</span></div>` : ""}
      ${ownerBuilder.signedByOwner ? `<div class="form-field"><span class="form-label">Signed by owner(s):</span><span class="form-value">${val(ownerBuilder.signedByOwner)}</span></div>` : ""}
    </div>
  </div>
    `;
    })()}

  ${(() => {
        const nature = formData.natureOfBuildingWork;
        if (!nature)
            return "";
        return `
  <div class="accordion-section">
    <div class="accordion-title">Nature of building work (tick if applicable or give other description):</div>
    <div class="checkbox-section">
      ${nature.constructionNewBuilding ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Construction of a new building</span></div>` : ""}
      ${nature.demolitionBuilding ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Demolition of a building</span></div>` : ""}
      ${nature.reErectionBuilding ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Re-erection of a building</span></div>` : ""}
      ${nature.alterationsExistingBuilding ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Alterations to an existing building</span></div>` : ""}
      ${nature.removalBuilding ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Removal of a building</span></div>` : ""}
      ${nature.extensionBuilding ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Extension to a building</span></div>` : ""}
      ${nature.tenancyFitout ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Tenancy fitout</span></div>` : ""}
      ${nature.changeOfUse ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Change of use of a building</span></div>` : ""}
      ${nature.otherDescriptionEnabled ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Other: ${val(nature.otherDescription)}</span></div>` : ""}
      ${nature.constructionSwimmingPoolOrSpa ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Construction of swimming pool or spa</span></div>` : ""}
      ${nature.constructionSwimmingPoolOrSpaBarrier ? `<div class="checkbox-label"><input type="checkbox" checked disabled /><span>Construction of swimming pool / spa barrier</span></div>` : ""}
    </div>
    ${nature.descriptionOfProposedBuildingWorks ? `
    <div class="form-field" style="margin-top: 15px;">
      <span class="form-label">Description of proposed building works and use of building to be included on the building permit:</span>
      <span class="form-value form-textarea">${val(nature.descriptionOfProposedBuildingWorks)}</span>
    </div>
    ` : ""}
  </div>
    `;
    })()}

  ${(() => {
        const details = formData.buildingDetails;
        if (!details)
            return "";
        return `
  <div class="accordion-section">
    <div class="accordion-title">Building details:</div>
    <div class="form-grid">
      ${details.proposedUseOfBuilding ? `<div class="form-field"><span class="form-label">Proposed use of building:</span><span class="form-value">${val(details.proposedUseOfBuilding)}</span></div>` : ""}
      ${details.proposedClassOfBuilding ? `<div class="form-field"><span class="form-label">Proposed class of building:</span><span class="form-value">${val(details.proposedClassOfBuilding)}</span></div>` : ""}
      ${details.currentUseOfBuilding ? `<div class="form-field"><span class="form-label">Current use of building:</span><span class="form-value">${val(details.currentUseOfBuilding)}</span></div>` : ""}
      ${details.currentClassOfBuilding ? `<div class="form-field"><span class="form-label">Class of building:</span><span class="form-value">${val(details.currentClassOfBuilding)}</span></div>` : ""}
    </div>
    <div class="form-note">* Last legal use of building</div>

    <div class="section-title" style="margin-top: 20px;">Social Housing</div>
    <div class="form-note">Does any of the building work include the construction of social housing as referred to in regulation 281B of the Building Regulations 2018?</div>
    <div class="form-field"><span class="form-value">${details.includesSocialHousing ? "Yes" : "No"}</span></div>

    <div class="section-title" style="margin-top: 20px;">Emergency Recovery</div>
    <div class="form-note">Does any of the building work include the construction of a dwelling that was destroyed or damaged in an emergency referred to in regulation 166J(b) of the Building Regulations 2018?</div>
    <div class="form-field"><span class="form-value">${details.isEmergencyRecoveryDwelling ? "Yes" : "No"}</span></div>
  </div>
    `;
    })()}

  ${(() => {
        const cost = formData.costOfBuildingWork;
        if (!cost)
            return "";
        const nonOwner = cost.nonOwnerBuilder;
        const ownerB = cost.ownerBuilder;
        const engagedBuildersHtml = (ownerB?.engagedBuilders ?? [])
            .map((b, i) => b?.amount ? `<div class="form-field"><span class="form-label">Builder ${i + 1} – contract price or estimate:</span><span class="form-value">${val(b.amount)}</span></div>` : "")
            .join("");
        return `
  <div class="accordion-section">
    <div class="accordion-title">Cost of the building work (2026 Requirement from the Building & Plumbing Commission)</div>
    <div class="form-note">Note: All costs specified in an application are to be inclusive of GST.</div>

    <div class="section-title" style="margin-top: 16px;">Part A – If application for a building permit is made by a person other than an owner-builder</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Contract or agreement (other than cost-plus)?</span><span class="form-value">${nonOwner?.hasLumpSumContract ? "Yes" : "No"}</span></div>
      ${nonOwner?.lumpSumContractPrice ? `<div class="form-field"><span class="form-label">Contract price or agreed amount:</span><span class="form-value">${val(nonOwner.lumpSumContractPrice)}</span></div>` : ""}
      <div class="form-field"><span class="form-label">Cost-plus contract or agreement?</span><span class="form-value">${nonOwner?.hasCostPlusContract ? "Yes" : "No"}</span></div>
      ${nonOwner?.costPlusEstimatedTotal ? `<div class="form-field"><span class="form-label">Estimated total:</span><span class="form-value">${val(nonOwner.costPlusEstimatedTotal)}</span></div>` : ""}
      ${nonOwner?.chattelsCost ? `<div class="form-field"><span class="form-label">Chattels cost:</span><span class="form-value">${val(nonOwner.chattelsCost)}</span></div>` : ""}
      ${nonOwner?.prescribedExcludedItemsCost ? `<div class="form-field"><span class="form-label">Prescribed excluded items cost:</span><span class="form-value">${val(nonOwner.prescribedExcludedItemsCost)}</span></div>` : ""}
    </div>

    <div class="section-title" style="margin-top: 16px;">Part B – Mixed classes of building (non-owner-builder)</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Does work relate to mixed classes (2-8 and 1,9,10)?</span><span class="form-value">${nonOwner?.relatesToMixedClasses ? "Yes" : "No"}</span></div>
      ${nonOwner?.relatesToMixedClassesNotes ? `<div class="form-field"><span class="form-label">Notes (Part B):</span><span class="form-value">${val(nonOwner.relatesToMixedClassesNotes)}</span></div>` : ""}
    </div>

    <div class="section-title" style="margin-top: 16px;">Part C – All stages of staged building work</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Is this a stage of whole building work?</span><span class="form-value">${nonOwner?.isStageOfWholeWork ? "Yes" : "No"}</span></div>
      ${nonOwner?.stageExtentDescription ? `<div class="form-field"><span class="form-label">Extent of the stage:</span><span class="form-value">${val(nonOwner.stageExtentDescription)}</span></div>` : ""}
      <div class="form-field"><span class="form-label">Contract (not cost-plus) for all stages?</span><span class="form-value">${nonOwner?.hasWholeLumpSumContract ? "Yes" : "No"}</span></div>
      ${nonOwner?.wholeLumpSumContractPrice ? `<div class="form-field"><span class="form-label">Contract price (all stages):</span><span class="form-value">${val(nonOwner.wholeLumpSumContractPrice)}</span></div>` : ""}
      <div class="form-field"><span class="form-label">Cost-plus contract for all stages?</span><span class="form-value">${nonOwner?.hasWholeCostPlusContract ? "Yes" : "No"}</span></div>
      ${nonOwner?.wholeCostPlusEstimatedTotal ? `<div class="form-field"><span class="form-label">Estimated total (all stages):</span><span class="form-value">${val(nonOwner.wholeCostPlusEstimatedTotal)}</span></div>` : ""}
      ${nonOwner?.wholeChattelsCost ? `<div class="form-field"><span class="form-label">Chattels cost (all stages):</span><span class="form-value">${val(nonOwner.wholeChattelsCost)}</span></div>` : ""}
      ${nonOwner?.wholePrescribedExcludedItemsCost ? `<div class="form-field"><span class="form-label">Prescribed excluded items cost (all stages):</span><span class="form-value">${val(nonOwner.wholePrescribedExcludedItemsCost)}</span></div>` : ""}
    </div>

    <div class="section-title" style="margin-top: 20px;">Part A – If application for a building permit is made by an owner-builder</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Intends to carry out work as owner-builder?</span><span class="form-value">${ownerB?.isOwnerBuilder ? "Yes" : "No"}</span></div>
      ${ownerB?.ownerBuilderCertificateOfConsentNo ? `<div class="form-field"><span class="form-label">Owner-builder certificate of consent no:</span><span class="form-value">${val(ownerB.ownerBuilderCertificateOfConsentNo)}</span></div>` : ""}
      ${ownerB?.ownerBuilderEstimatedCost ? `<div class="form-field"><span class="form-label">Owner-builder's estimated cost:</span><span class="form-value">${val(ownerB.ownerBuilderEstimatedCost)}</span></div>` : ""}
    </div>

    <div class="section-title" style="margin-top: 16px;">Part B – Owner-builder engaging builders</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Will owner-builder engage one or more builders?</span><span class="form-value">${ownerB?.ownerEngagesBuilders ? "Yes" : "No"}</span></div>
      ${engagedBuildersHtml}
    </div>

    <div class="section-title" style="margin-top: 16px;">Part C – Costs relating to chattels and prescribed excluded items</div>
    <div class="form-grid">
      ${ownerB?.ownerChattelsCost ? `<div class="form-field"><span class="form-label">Chattels cost (Part A and/or B):</span><span class="form-value">${val(ownerB.ownerChattelsCost)}</span></div>` : ""}
      ${ownerB?.ownerPrescribedExcludedItemsCost ? `<div class="form-field"><span class="form-label">Prescribed excluded items cost:</span><span class="form-value">${val(ownerB.ownerPrescribedExcludedItemsCost)}</span></div>` : ""}
    </div>

    <div class="section-title" style="margin-top: 16px;">Part D – All stages of staged building work by an owner-builder</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Is this a stage of whole building work?</span><span class="form-value">${ownerB?.ownerIsStageOfWholeWork ? "Yes" : "No"}</span></div>
      ${ownerB?.ownerStageExtentDescription ? `<div class="form-field"><span class="form-label">Extent of the stage:</span><span class="form-value">${val(ownerB.ownerStageExtentDescription)}</span></div>` : ""}
      ${ownerB?.ownerWholeEstimatedCost ? `<div class="form-field"><span class="form-label">Owner-builder's estimated cost (all stages):</span><span class="form-value">${val(ownerB.ownerWholeEstimatedCost)}</span></div>` : ""}
      ${ownerB?.ownerWholeChattelsCost ? `<div class="form-field"><span class="form-label">Chattels cost (all stages):</span><span class="form-value">${val(ownerB.ownerWholeChattelsCost)}</span></div>` : ""}
      ${ownerB?.ownerWholePrescribedExcludedItemsCost ? `<div class="form-field"><span class="form-label">Prescribed excluded items cost (all stages):</span><span class="form-value">${val(ownerB.ownerWholePrescribedExcludedItemsCost)}</span></div>` : ""}
      <div class="form-field"><span class="form-label">Contract (not cost-plus) with builder for all stages?</span><span class="form-value">${ownerB?.ownerHasLumpSumBuilderForStages ? "Yes" : "No"}</span></div>
      ${ownerB?.ownerLumpSumStagesPrice ? `<div class="form-field"><span class="form-label">Contract price (all stages):</span><span class="form-value">${val(ownerB.ownerLumpSumStagesPrice)}</span></div>` : ""}
      <div class="form-field"><span class="form-label">Cost-plus contract with builder for all stages?</span><span class="form-value">${ownerB?.ownerHasCostPlusBuilderForStages ? "Yes" : "No"}</span></div>
      ${ownerB?.ownerCostPlusStagesEstimate ? `<div class="form-field"><span class="form-label">Estimated total (all stages):</span><span class="form-value">${val(ownerB.ownerCostPlusStagesEstimate)}</span></div>` : ""}
      ${ownerB?.ownerStagesChattelsCost ? `<div class="form-field"><span class="form-label">Chattels cost under builder contract (stages):</span><span class="form-value">${val(ownerB.ownerStagesChattelsCost)}</span></div>` : ""}
      ${ownerB?.ownerStagesExcludedItemsCost ? `<div class="form-field"><span class="form-label">Prescribed excluded items cost (stages):</span><span class="form-value">${val(ownerB.ownerStagesExcludedItemsCost)}</span></div>` : ""}
    </div>

    <div class="section-title" style="margin-top: 16px;">Part E – Mixed classes of building (owner-builder)</div>
    <div class="form-grid">
      <div class="form-field"><span class="form-label">Does work relate to mixed classes (2-8 and 1,9,10)?</span><span class="form-value">${ownerB?.ownerRelatesToMixedClasses ? "Yes" : "No"}</span></div>
      ${ownerB?.ownerRelatesToMixedClassesNotes ? `<div class="form-field"><span class="form-label">Notes (Part E):</span><span class="form-value">${val(ownerB.ownerRelatesToMixedClassesNotes)}</span></div>` : ""}
    </div>
  </div>
    `;
    })()}

  ${(() => {
        const sig = formData.signatureDeclaration;
        if (!sig)
            return "";
        return `
  <div class="accordion-section">
    <div class="accordion-title">Signature & declaration (refer to conditions below)</div>
    <div class="form-grid">
      ${sig.applicantSignature ? `<div class="form-field"><span class="form-label">Applicant's signature:</span><span class="form-value">${val(sig.applicantSignature)}</span></div>` : ""}
      ${sig.printName ? `<div class="form-field"><span class="form-label">Print name:</span><span class="form-value">${val(sig.printName)}</span></div>` : ""}
      ${sig.signatureDate ? `<div class="form-field"><span class="form-label">Date:</span><span class="form-value">${val(sig.signatureDate)}</span></div>` : ""}
    </div>
  </div>
    `;
    })()}

  ${autoPrint ? `<script>window.onload = function() { window.print(); };</script>` : ""}
</body>
</html>`;
}
/**
 * Maps BuildingPermitFormData (from job notes) to FormDataOverrides for merging
 * into form.data when generating the printed permit.
 * Also passes through any additionalFormData fields stored in notes.
 */
export function notesToFormDataOverrides(notes) {
    const baseOverrides = {
        recipient: notes.selectedSurveyor || undefined,
        applicantRole: notes.applicantRole || undefined,
        applicantAcnArbn: notes.applicantAcnArbn || undefined,
        contactName: notes.applicant.contactName || undefined,
        email: notes.applicant.email || undefined,
        companyName: notes.applicant.companyName || undefined,
        phone: notes.applicant.phone || undefined,
        address: notes.applicant.address || undefined,
        servingAddress: notes.applicant.documentAddress || undefined,
        propertyDetails: notes.propertyDetails
            ? {
                buildingName: notes.propertyDetails.buildingName || undefined,
                shopNo: notes.propertyDetails.shopNo || undefined,
                fullAddress: notes.propertyDetails.fullAddress || undefined,
                lotNumber: notes.propertyDetails.lotNumber || undefined,
                lpPs: notes.propertyDetails.lpPs || undefined,
                vol: notes.propertyDetails.vol || undefined,
                fol: notes.propertyDetails.fol || undefined,
                crownAllot: notes.propertyDetails.crownAllot || undefined,
                section: notes.propertyDetails.section || undefined,
                parish: notes.propertyDetails.parish || undefined,
                county: notes.propertyDetails.county || undefined,
                council: notes.propertyDetails.council || undefined,
                landOwnedByCrownOrPublicAuthority: notes.propertyDetails.landOwnedByCrownOrPublicAuthority,
            }
            : undefined,
        buildingOwner: notes.owner
            ? {
                name: notes.owner.name || undefined,
                contactPerson: notes.owner.contactPerson || undefined,
                email: notes.owner.email || undefined,
                company: notes.owner.companyName || undefined,
                phone: notes.owner.phone || undefined,
                address: notes.owner.address || undefined,
                acnArbn: notes.owner.acnArbn || undefined,
            }
            : undefined,
        tenant: notes.tenant
            ? {
                contactPerson: notes.tenant.contactPerson || undefined,
                email: notes.tenant.email || undefined,
                company: notes.tenant.companyName || undefined,
                phone: notes.tenant.phone || undefined,
                address: notes.tenant.address || undefined,
            }
            : undefined,
        builder: notes.builder
            ? {
                name: notes.builder.name || undefined,
                contactPerson: notes.builder.contactPerson || undefined,
                email: notes.builder.email || undefined,
                company: notes.builder.companyName || undefined,
                phone: notes.builder.phone || undefined,
                address: notes.builder.address || undefined,
                practitionerNo: notes.builder.practitionerNo || undefined,
                acnArbn: notes.builder.acnArbn || undefined,
                postalAddress: notes.builder.postalAddress || undefined,
                naturalPerson: notes.builder.naturalPerson
                    ? {
                        contactPerson: notes.builder.naturalPerson.contactPerson || undefined,
                        email: notes.builder.naturalPerson.email || undefined,
                        address: notes.builder.naturalPerson.address || undefined,
                        phone: notes.builder.naturalPerson.phone || undefined,
                    }
                    : undefined,
            }
            : undefined,
        lesseeResponsible: notes.lesseeResponsible,
    };
    if (notes.additionalFormData) {
        return {
            ...notes.additionalFormData,
            ...baseOverrides,
        };
    }
    return baseOverrides;
}
